import {getVideoSrcFromFile, getMarkerRequestKey, getClipRequestKey, getCustomFrameRequestKey} from '~/utils/video-editor/clip-utils.js';
import {blobToDataURL} from '~/utils/video-editor/upload-utils.js';
import {useVideoFrameCapture} from '~/composables/video-editor/useVideoFrameCapture.js';

export function useVideoEditorStoreSnapshots({clips, videoSrc, file}) {
  let _markerFrameCapture = null;
  let _markerSnapshotRequestByMarkerId = new Map();
  let _markerBatchRequestByClipId = new Map();
  let _customFrameSnapshotRequestByProjectId = new Map();

  function ensureFrameCapture() {
    if (!_markerFrameCapture) {
      _markerFrameCapture = useVideoFrameCapture();
    }
    return _markerFrameCapture;
  }

  function getSource() {
    return videoSrc.value || getVideoSrcFromFile(file.value);
  }

  function loadSnapshotSource(src) {
    const source = src || getSource();
    if (!source) return;

    ensureFrameCapture().loadSource(source).catch(() => {});
  }

  async function refreshMarkerSnapshot({marker, time} = {}) {
    const markerRequestKey = getMarkerRequestKey(marker);
    const source = getSource();
    const markerTime = Number(typeof time === 'number' ? time : marker?.time);

    if (!markerRequestKey || !source || !Number.isFinite(markerTime)) {
      return null;
    }

    const frameCapture = ensureFrameCapture();

    const requestToken = Symbol(markerRequestKey);
    _markerSnapshotRequestByMarkerId.set(markerRequestKey, requestToken);

    const imageSrc = await frameCapture.captureAt({
      src: source,
      timeSec: markerTime,
      maxDimension: VIDEO_EDITOR_PREVIEW_FRAME_MAX_DIMENSION,
      quality: 0.85,
      queueKey: `marker:${markerRequestKey}`,
      replacePending: true
    });

    if (_markerSnapshotRequestByMarkerId.get(markerRequestKey) !== requestToken) {
      return null;
    }

    if (imageSrc) {
      marker.snapshot = imageSrc;
    }

    return imageSrc;
  }

  async function refreshClipMarkerSnapshots({clip} = {}) {
    const source = getSource();
    const clipRequestKey = getClipRequestKey(clip);
    const markers = (clip?.markers || [])
      .filter(marker => marker && Number.isFinite(Number(marker.time)));

    if (!clipRequestKey || !source || !markers.length) {
      return [];
    }

    const frameCapture = ensureFrameCapture();

    const requestToken = Symbol(clipRequestKey);
    _markerBatchRequestByClipId.set(clipRequestKey, requestToken);

    for (const marker of markers) {
      const markerRequestKey = getMarkerRequestKey(marker);
      if (markerRequestKey) {
        _markerSnapshotRequestByMarkerId.set(markerRequestKey, requestToken);
      }
    }

    const results = await frameCapture.captureBatch({
      src: source,
      captures: markers.map(marker => ({
        timeSec: Number(marker.time),
        maxDimension: VIDEO_EDITOR_PREVIEW_FRAME_MAX_DIMENSION,
        quality: 0.85
      })),
      queueKey: `clip:${clipRequestKey}`,
      replacePending: true
    });

    if (_markerBatchRequestByClipId.get(clipRequestKey) !== requestToken) {
      return results;
    }

    markers.forEach((marker, index) => {
      const markerRequestKey = getMarkerRequestKey(marker);
      if (!markerRequestKey || _markerSnapshotRequestByMarkerId.get(markerRequestKey) !== requestToken) {
        return;
      }

      const imageSrc = results[index]?.imageSrc;
      if (imageSrc) {
        marker.snapshot = imageSrc;
      }
    });

    return results;
  }

  async function hydrateAllMarkerSnapshots() {
    const clipsToHydrate = clips.value.slice();

    for (const clip of clipsToHydrate) {
      await refreshClipMarkerSnapshots({clip});
    }
  }

  async function refreshCustomFrameSnapshot({frame, maxWidth = VIDEO_EDITOR_PREVIEW_FRAME_MAX_DIMENSION} = {}) {
    const requestKey = getCustomFrameRequestKey(frame);

    if (!requestKey) {
      return null;
    }

    const requestToken = Symbol(requestKey);
    _customFrameSnapshotRequestByProjectId.set(requestKey, requestToken);

    const projectExport = useProjectExport();
    const exportedSurface = await projectExport.exportSurface({
      projectId: frame.design_project_id,
      surfaceIndex: 0,
      maxWidth,
      exportType: VIDEO_EDITOR_CUSTOM_FRAME_SNAPSHOT_EXPORT_TYPE
    });
    const imageSrc = await blobToDataURL(exportedSurface.blob);

    if (_customFrameSnapshotRequestByProjectId.get(requestKey) !== requestToken) {
      return null;
    }

    frame.thumbnail = imageSrc;
    return imageSrc;
  }

  async function refreshClipCustomFrameSnapshots({clip} = {}) {
    const titleFrame = clip?.frames?.title;
    const customFrames = titleFrame?.design_project_id != null ? [titleFrame] : [];

    if (!customFrames.length) {
      return [];
    }

    const results = [];

    for (const frame of customFrames) {
      try {
        results.push(await refreshCustomFrameSnapshot({frame}));
      } catch (error) {
        console.warn('Failed to refresh custom frame thumbnail', error);
        results.push(null);
      }
    }

    return results;
  }

  async function hydrateAllCustomFrameSnapshots() {
    const clipsToHydrate = clips.value.slice();

    for (const clip of clipsToHydrate) {
      await refreshClipCustomFrameSnapshots({clip});
    }
  }

  function disposeSnapshots() {
    _markerFrameCapture?.dispose?.();
    _markerFrameCapture = null;
    _markerSnapshotRequestByMarkerId = new Map();
    _markerBatchRequestByClipId = new Map();
    _customFrameSnapshotRequestByProjectId = new Map();
  }

  return {
    loadSnapshotSource,
    refreshMarkerSnapshot,
    refreshClipMarkerSnapshots,
    hydrateAllMarkerSnapshots,
    refreshCustomFrameSnapshot,
    refreshClipCustomFrameSnapshots,
    hydrateAllCustomFrameSnapshots,
    disposeSnapshots
  };
}
